import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export interface MobileHeaderConfig {
  title?: string;
  description?: string;
  back?: () => void;
  h1?: string;
  subtitle?: string;
  alternativeHeader?: React.ReactNode;
  /** Shown in place of the EasySBC logo, e.g. My Club's crest. */
  icon?: string;
}

interface MobileHeaderContextValue {
  header: MobileHeaderConfig | null;
  setHeader: (config: MobileHeaderConfig | null) => void;
}

const MobileHeaderContext = createContext<MobileHeaderContextValue | null>(
  null,
);

export const MobileHeaderProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [header, setHeader] = useState<MobileHeaderConfig | null>(null);
  const value = useMemo(() => ({ header, setHeader }), [header]);
  return (
    <MobileHeaderContext.Provider value={value}>
      {children}
    </MobileHeaderContext.Provider>
  );
};

export const useMobileHeader = () => {
  const ctx = useContext(MobileHeaderContext);
  if (!ctx) {
    throw new Error(
      "useMobileHeader must be used within a MobileHeaderProvider",
    );
  }
  return ctx;
};

/**
 * Registers this page's header content on the persistent mobile top nav bar.
 * Clears it on unmount. `back` and `alternativeHeader` are intentionally part of
 * the effect deps so the bar re-renders with a fresh node (e.g. DetailedPlayer's
 * scroll-reactive alternativeHeader) — the setter is stable, so this never loops.
 */
export const usePageHeader = (config: MobileHeaderConfig) => {
  const ctx = useContext(MobileHeaderContext);
  const setHeader = ctx?.setHeader;
  const { title, description, back, h1, subtitle, alternativeHeader, icon } =
    config;

  const stableSetHeader = useCallback(
    (c: MobileHeaderConfig | null) => setHeader?.(c),
    [setHeader],
  );

  useEffect(() => {
    stableSetHeader({
      title,
      description,
      back,
      h1,
      subtitle,
      alternativeHeader,
      icon,
    });
    return () => stableSetHeader(null);
  }, [
    stableSetHeader,
    title,
    description,
    back,
    h1,
    subtitle,
    alternativeHeader,
    icon,
  ]);
};
